import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

function today() { return new Date().toISOString().slice(0, 10); }
function isOverdue(t) { return t.status === "open" && t.due_date && t.due_date < today(); }
function isDueToday(t) { return t.status === "open" && t.due_date === today(); }

export default function FollowUps() {
  const [tasks, setTasks] = useState([]);
  const [statusF, setStatusF] = useState("open");
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/crm/tasks", { params: { status: statusF } })
      .then((r) => setTasks(r.data))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, [statusF]);

  async function toggle(t) {
    try {
      await api.put(`/crm/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
      refresh();
    } catch { toast.error("Cannot update task"); }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">CRM</p>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">Follow-ups</h1>
          <p className="text-sm text-slate-500 mt-1">All open tasks across every lead, sorted by due date.</p>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          {["open", "done"].map((s) => (
            <button key={s} onClick={() => setStatusF(s)} data-testid={`filter-${s}`}
              className={`px-3 py-1.5 text-sm capitalize ${statusF === s ? "bg-[#0D1B2A] text-white" : "bg-white text-slate-600"}`}>{s}</button>
          ))}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm"><CardContent className="p-4 overflow-x-auto">
        {loading ? <p className="text-sm text-slate-500 py-8 text-center">Loading…</p> :
        tasks.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center" data-testid="empty-tasks">No {statusF} tasks.</p> :
        (<table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="w-8"></th><th className="text-left py-3 px-2">Task</th>
            <th className="text-left py-3 px-2">Lead</th><th className="text-left py-3 px-2">Due</th>
          </tr></thead>
          <tbody>
            {tasks.map((t) => {
              const over = isOverdue(t), tod = isDueToday(t);
              return (
                <tr key={t.id} className={`border-b border-slate-100 ${over ? "bg-red-50" : tod ? "bg-amber-50" : ""}`} data-testid={`task-row-${t.id}`}>
                  <td className="py-3 px-2"><Checkbox checked={t.status === "done"} onCheckedChange={() => toggle(t)} data-testid={`toggle-${t.id}`} /></td>
                  <td className="py-3 px-2 text-[#0D1B2A]">{t.description}</td>
                  <td className="py-3 px-2"><Link to="/crm" className="text-[#1A7F6E] hover:underline">{t.org_name}</Link></td>
                  <td className={`py-3 px-2 text-sm ${over ? "text-[#DC2626] font-semibold" : tod ? "text-amber-700 font-semibold" : "text-slate-600"}`}>
                    {t.due_date} {over && <span className="text-xs">(overdue)</span>} {tod && <span className="text-xs">(today)</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>)}
      </CardContent></Card>
    </div>
  );
}

export function TaskAlertCard() {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/crm/tasks/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  if (!summary) return null;
  const showAlert = summary.overdue > 0 || summary.due_today > 0;
  if (!showAlert) return null;
  return (
    <Card className="border-red-200 bg-red-50 shadow-sm" data-testid="task-alerts">
      <CardContent className="p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#7F1D1D]">
            {summary.overdue > 0 && <>{summary.overdue} overdue </>}{summary.due_today > 0 && <>· {summary.due_today} due today</>}
          </p>
          <Link to="/follow-ups" className="text-xs text-[#DC2626] hover:underline">View follow-ups →</Link>
        </div>
        <Button asChild variant="outline" size="sm"><Link to="/follow-ups" data-testid="alert-view-tasks">Open</Link></Button>
      </CardContent>
    </Card>
  );
}
